/**
 * Portfolio / Vault — The Student's Full Story
 * Pulls from ALL platform data to create a living portfolio
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FolderOpen, Upload, FileText, Image, Award, Calendar,
  Star, Trash2, Plus, Share2, ArrowLeft, Heart, Target,
  Briefcase, Zap, Users, GraduationCap, CheckCircle,
  Trophy, ChevronRight, Brain, X, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Navigation from '@/components/Navigation';
import { formatDisplayName } from '@/utils/formatText';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const ITEM_TYPES = [
  { value: 'certificate', label: 'Certificate', icon: '🏆' },
  { value: 'project', label: 'Project', icon: '💼' },
  { value: 'achievement', label: 'Achievement', icon: '🏅' },
  { value: 'document', label: 'Document', icon: '📄' },
  { value: 'photo', label: 'Photo', icon: '📸' },
  { value: 'skill', label: 'Skill Evidence', icon: '⭐' }
];

const VaultPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [story, setStory] = useState(null);
  const [vaultItems, setVaultItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', type: 'achievement', url: '' });
  const [resumeText, setResumeText] = useState('');
  const [generatingResume, setGeneratingResume] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [storyRes, vaultRes] = await Promise.all([
        fetch(`${API}/api/gps/portfolio-story`, { headers: hdr() }),
        fetch(`${API}/api/vault/my-items`, { headers: hdr() })
      ]);
      if (storyRes.ok) setStory(await storyRes.json());
      if (vaultRes.ok) {
        const vaultData = await vaultRes.json();
        setVaultItems(Array.isArray(vaultData) ? vaultData : vaultData.items || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const uploadItem = async () => {
    if (!form.title) { toast.error('Enter a title'); return; }
    try {
      const res = await fetch(`${API}/api/vault/add-item`, {
        method: 'POST', headers: hdr(), body: JSON.stringify(form)
      });
      if (res.ok) { toast.success('Added to portfolio!'); setShowUpload(false); setForm({ title: '', description: '', type: 'achievement', url: '' }); fetchAll(); }
    } catch (e) { toast.error('Failed to add'); }
  };

  const deleteItem = async (id) => {
    if (!confirm('Remove this item?')) return;
    try {
      await fetch(`${API}/api/vault/items/${id}`, { method: 'DELETE', headers: hdr() });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const generateResume = async () => {
    setGeneratingResume(true);
    try {
      const res = await fetch(`${API}/api/gps/resume-builder/generate`, { method: 'POST', headers: hdr() });
      if (res.ok) { const d = await res.json(); setResumeText(d.resume_text || ''); toast.success('Resume generated!'); }
    } catch (e) { toast.error('Failed to generate'); }
    setGeneratingResume(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
      <FolderOpen className="w-12 h-12 animate-pulse text-blue-500" />
    </div>
  );

  const s = story || {};
  const u = s.user || {};

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Hero — The Student's Identity Card */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)' }}>
        <div className="relative max-w-3xl mx-auto p-6 pb-8">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-white/10 hover:bg-white/20" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-bold text-white">My Portfolio</h1>
            <Button size="sm" onClick={() => setShowUpload(true)} className="ml-auto bg-white/15 hover:bg-white/25 text-white border border-white/20 text-xs rounded-lg">
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>

          {/* Identity Card */}
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center text-3xl border border-white/20">{u.avatar_emoji || '🎓'}</div>
            <div>
              <h2 className="text-2xl font-bold text-white">{u.name || user?.name}</h2>
              <p className="text-white/60 text-sm">{user?.program_name || 'YAS Connect'}</p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-5 gap-2">
            {[
              { label: 'Points', val: u.total_points || 0, icon: Star },
              { label: 'Interests', val: (s.interests || []).length, icon: Heart },
              { label: 'Skills', val: (s.skills || []).length, icon: Zap },
              { label: 'Goals Hit', val: (s.completed_goals || []).length, icon: Trophy },
              { label: 'Careers', val: (s.career_explorations || []).length, icon: Briefcase },
            ].map(st => (
              <div key={st.label} className="text-center p-2 rounded-lg bg-white/10">
                <st.icon className="w-3.5 h-3.5 mx-auto mb-0.5 text-white/70" />
                <p className="text-sm font-bold text-white">{st.val}</p>
                <p className="text-[9px] text-white/50">{st.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-orange-500/15" />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Interests & Skills */}
        {((s.interests || []).length > 0 || (s.skills || []).length > 0) && (
          <Card className="p-4 rounded-2xl bg-white shadow-sm">
            <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-pink-500" /> Who I Am</h3>
            {(s.interests || []).length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Interests</p>
                <div className="flex flex-wrap gap-1">{s.interests.map((i, idx) => <Badge key={idx} className="text-xs bg-pink-50 text-pink-700 border border-pink-200">{formatDisplayName(i)}</Badge>)}</div>
              </div>
            )}
            {(s.skills || []).length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Skills</p>
                <div className="flex flex-wrap gap-1">{s.skills.map((sk, idx) => <Badge key={idx} className="text-xs bg-amber-50 text-amber-700 border border-amber-200">{formatDisplayName(sk)}</Badge>)}</div>
              </div>
            )}
          </Card>
        )}

        {/* Career Direction */}
        {(s.career_explorations || []).length > 0 && (
          <Card className="p-4 rounded-2xl bg-white shadow-sm">
            <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4 text-orange-500" /> Where I'm Headed</h3>
            {s.career_explorations.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-orange-50 mb-2">
                <Briefcase className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-orange-800">{c.title}</p>
                  <p className="text-[10px] text-orange-600">{c.salary} | {c.education}</p>
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Activities & Experience */}
        {(s.activities || []).length > 0 && (
          <Card className="p-4 rounded-2xl bg-white shadow-sm">
            <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> What I Do</h3>
            {s.activities.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-blue-50 mb-1.5">
                <span className="text-sm">{a.category === 'sport' ? '⚽' : a.category === 'club' ? '🎭' : a.category === 'job' ? '💼' : '📌'}</span>
                <div><p className="text-sm font-semibold text-blue-800">{a.title}</p><p className="text-[10px] text-blue-600">{a.role} {a.organization ? `at ${a.organization}` : ''}</p></div>
              </div>
            ))}
          </Card>
        )}

        {/* Goals */}
        {((s.active_goals || []).length > 0 || (s.completed_goals || []).length > 0) && (
          <Card className="p-4 rounded-2xl bg-white shadow-sm">
            <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-emerald-500" /> What I'm Working Toward</h3>
            {(s.completed_goals || []).map((g, i) => (
              <div key={`c${i}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 mb-1.5">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-emerald-800">{g.title}</p>
                <Badge className="ml-auto text-[9px] bg-emerald-100 text-emerald-700">Completed</Badge>
              </div>
            ))}
            {(s.active_goals || []).map((g, i) => (
              <div key={`a${i}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 mb-1.5">
                <Target className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-gray-800">{g.title}</p>
                <Badge className="ml-auto text-[9px] bg-blue-100 text-blue-700">In Progress</Badge>
              </div>
            ))}
          </Card>
        )}

        {/* Post-Secondary Plans */}
        {(s.post_secondary || []).length > 0 && (
          <Card className="p-4 rounded-2xl bg-white shadow-sm">
            <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-indigo-500" /> After High School</h3>
            {s.post_secondary.map((p, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-indigo-50 mb-1.5">
                <p className="text-sm font-semibold text-indigo-800">{p.name}</p>
                <p className="text-[10px] text-indigo-600">{p.type?.replace('_', ' ')} | {p.status?.replace('_', ' ')}</p>
              </div>
            ))}
          </Card>
        )}

        {/* Milestones Timeline */}
        {(s.milestones || []).length > 0 && (
          <Card className="p-4 rounded-2xl bg-white shadow-sm">
            <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> My Journey</h3>
            <div className="space-y-3">
              {s.milestones.map((m, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${m.type === 'goal_completed' ? 'bg-emerald-500' : m.type === 'workshop' ? 'bg-orange-500' : 'bg-amber-500'}`} />
                    {i < s.milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                  </div>
                  <div className="pb-3">
                    <p className="text-sm font-semibold text-gray-800">{m.title}</p>
                    {m.date && <p className="text-[10px] text-gray-400">{new Date(m.date).toLocaleDateString()}</p>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* My Story */}
        <Card className="p-5 rounded-2xl bg-white shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2"><FileText className="w-4 h-4 text-orange-500" /> My Story</h3>
              <p className="text-[10px] text-gray-400">Your full personal narrative — built from everything you do on this platform</p>
            </div>
            <Button size="sm" onClick={generateResume} disabled={generatingResume}
              className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8 rounded-lg" data-testid="generate-resume-btn">
              {generatingResume ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Writing...</> : <><Brain className="w-3 h-3 mr-1" /> Generate My Story</>}
            </Button>
          </div>
          {resumeText ? (
            <div>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 mb-3">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{resumeText}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => {
                  const blob = new Blob([resumeText], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `${user?.name || 'my'}_story.txt`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Story downloaded!');
                }} data-testid="download-resume">
                  <Share2 className="w-3 h-3 mr-1" /> Download
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => {
                  navigator.clipboard.writeText(resumeText);
                  toast.success('Copied to clipboard!');
                }}>
                  Copy Text
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 bg-orange-50 rounded-xl">
              <FileText className="w-8 h-8 mx-auto mb-2 text-orange-300" />
              <p className="text-sm text-gray-600">AI will write your full personal story using everything you have done on this platform</p>
              <p className="text-xs text-orange-600 font-medium mt-1">The more you do — workshops, goals, wins, reflections — the richer your story becomes</p>
            </div>
          )}
        </Card>

        {/* Vault Items (Uploaded) */}
        <Card className="p-4 rounded-2xl bg-white shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2"><FolderOpen className="w-4 h-4 text-blue-500" /> My Vault</h3>
            <Button size="sm" onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700 text-gray-900 text-xs h-7 rounded-lg"><Plus className="w-3 h-3 mr-1" /> Add</Button>
          </div>
          {vaultItems.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-xl">
              <FolderOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">Add certificates, projects, and work samples</p>
              <p className="text-xs text-blue-600 font-medium mt-1">Everything here builds your story</p>
            </div>
          ) : (
            <div className="space-y-2">{vaultItems.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                <span className="text-lg">{ITEM_TYPES.find(t => t.value === item.type)?.icon || '📄'}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                  {item.description && <p className="text-[10px] text-gray-500">{item.description.slice(0, 60)}</p>}
                </div>
                <button onClick={() => deleteItem(item.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" /></button>
              </div>
            ))}</div>
          )}
        </Card>

        {/* Empty state nudge */}
        {!s.interests?.length && !s.career_explorations?.length && !s.activities?.length && (
          <Card className="p-5 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)' }}>
            <Brain className="w-10 h-10 mx-auto mb-3 text-orange-500" />
            <h3 className="font-bold text-gray-900 mb-1">Your story starts here</h3>
            <p className="text-sm text-gray-600 mb-3">The more you explore on the platform — interests, skills, goals, careers — the richer your portfolio becomes. Everything you do builds your story.</p>
            <Button onClick={() => navigate('/ai-guide')} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl">
              <Brain className="w-4 h-4 mr-2" /> Start with AI Guide
            </Button>
          </Card>
        )}
      </div>

      <Navigation role="youth" />

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-blue-500" /> Add to Portfolio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="What are you adding?" className="mt-1" /></div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Describe what this is..." className="mt-1" rows={2} /></div>
            <div><Label>Link (optional)</Label><Input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." className="mt-1" /></div>
            <Button onClick={uploadItem} className="w-full bg-blue-600 hover:bg-blue-700">Add to Portfolio</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VaultPage;
